import { useEffect, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { LIST_PROJECT_MILESTONES, LIST_PROJECT_OUTCOMES } from '../graphql/queries';
import type { ProjectMilestone, ProjectOutcome, ServiceAssignment } from '../graphql/types';

export interface ProjectAssignmentNameMaps {
  /** ProjectMilestone entityId -> name */
  milestoneNames: Record<string, string>;
  /** ProjectOutcome entityId -> name */
  outcomeNames: Record<string, string>;
  loading: boolean;
}

/**
 * Resolves milestone + outcome names for a list of Service -> ProjectMilestone
 * assignments, so callers can render human-readable names instead of raw ids
 * (issue #535).
 *
 * Batches exactly one `projectMilestones` + `projectOutcomes` query pair per
 * distinct `projectId` present in `assignments` (there is no single-entity
 * lookup-by-id query for milestones/outcomes in the GraphQL schema — only
 * per-project list queries). Project *names* are intentionally not resolved
 * here: callers already load the full project list via `useProjects()` for
 * the assign form's project picker, so a name lookup map can be built from
 * that data without an extra round-trip.
 */
export function useProjectAssignmentNames(
  assignments: ServiceAssignment[]
): ProjectAssignmentNameMaps {
  const apolloClient = useApolloClient();
  const [milestoneNames, setMilestoneNames] = useState<Record<string, string>>({});
  const [outcomeNames, setOutcomeNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // Stable primitive key so the effect only re-runs when the *set* of
  // projects referenced by the assignments actually changes.
  const projectIdsKey = [...new Set(assignments.map((a) => a.projectId).filter(Boolean))]
    .sort()
    .join(',');

  useEffect(() => {
    const projectIds = projectIdsKey ? projectIdsKey.split(',') : [];

    if (projectIds.length === 0) {
      setMilestoneNames({});
      setOutcomeNames({});
      return;
    }

    let cancelled = false;
    setLoading(true);

    Promise.all(
      projectIds.map((projectId) =>
        Promise.all([
          apolloClient.query<{ projectMilestones: ProjectMilestone[] }>({
            query: LIST_PROJECT_MILESTONES,
            variables: { projectId },
          }),
          apolloClient.query<{ projectOutcomes: ProjectOutcome[] }>({
            query: LIST_PROJECT_OUTCOMES,
            variables: { projectId },
          }),
        ])
      )
    )
      .then((results) => {
        if (cancelled) return;
        const nextMilestoneNames: Record<string, string> = {};
        const nextOutcomeNames: Record<string, string> = {};
        for (const [milestonesResult, outcomesResult] of results) {
          for (const milestone of milestonesResult.data?.projectMilestones ?? []) {
            nextMilestoneNames[milestone.entityId] = milestone.name;
          }
          for (const outcome of outcomesResult.data?.projectOutcomes ?? []) {
            nextOutcomeNames[outcome.entityId] = outcome.name;
          }
        }
        setMilestoneNames(nextMilestoneNames);
        setOutcomeNames(nextOutcomeNames);
      })
      .catch(() => {
        // Best-effort resolution — UI falls back to raw ids on failure.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apolloClient, projectIdsKey]);

  return { milestoneNames, outcomeNames, loading };
}
